import React, { useState } from 'react';
import { Layout, Button, Dropdown, Avatar, Space, Typography, Badge, List, Empty, Spin, Tooltip } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  BellOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';
import { useNotification } from '../../contexts/NotificationContext';
import '../../styles/notification.css';

const { Header: AntHeader } = Layout;
const { Text } = Typography;

const Header: React.FC = () => {
  const { state, toggleSidebar, logout } = useApp();
  const { state: notificationState, actions: notificationActions } = useNotification();
  const navigate = useNavigate();
  const [notificationDropdownVisible, setNotificationDropdownVisible] = useState(false);

  const handleLogout = async () => {
    await logout();
  };

  // 处理通知点击
  const handleNotificationClick = () => {
    if (notificationState.unreadCount > 0) {
      // 标记所有未读通知为已读
      const unreadNotifications = notificationState.recentNotifications
        .filter(n => !n.read_at)
        .map(n => n.id);
      
      if (unreadNotifications.length > 0) {
        notificationActions.markAsRead(unreadNotifications);
      }
    }
    setNotificationDropdownVisible(!notificationDropdownVisible);
  };

  // 获取通知图标
  const getNotificationIcon = (triggerType: string, status: string) => {
    if (status === 'failed') {
      return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
    }
    
    switch (triggerType) {
      case 'failure':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'recovery':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'test':
        return <ClockCircleOutlined style={{ color: '#1890ff' }} />;
      default:
        return <BellOutlined style={{ color: '#666' }} />;
    }
  };

  // 格式化通知时间
  const formatNotificationTime = (time: string) => {
    const now = new Date();
    const notificationTime = new Date(time);
    const diffInMinutes = Math.floor((now.getTime() - notificationTime.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) {
      return '刚刚';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}分钟前`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}小时前`;
    } else {
      return notificationTime.toLocaleDateString('zh-CN');
    }
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人设置',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '系统设置',
      onClick: () => navigate('/settings'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  // 创建通知下拉菜单内容
  const notificationDropdown = (
    <div style={{ 
      width: 320, 
      maxHeight: 400, 
      overflow: 'hidden',
      borderRadius: '8px',
      backgroundColor: '#ffffff',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
    }}>
      <div 
        className="notification-dropdown-header"
        style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Text strong style={{ fontSize: '14px', color: '#262626' }}>通知</Text>
        {notificationState.unreadCount > 0 && (
          <Button 
            type="link" 
            size="small"
            style={{ 
              fontSize: '12px',
              padding: '0 4px',
              height: 'auto',
              color: '#1890ff'
            }}
            onClick={(e) => {
              e.stopPropagation();
              notificationActions.markAllAsRead();
            }}
          >
            全部已读
          </Button>
        )}
      </div>
      
      <div style={{ maxHeight: '300px', overflow: 'auto' }}>
        {notificationState.loading ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <Spin size="small" />
          </div>
        ) : notificationState.recentNotifications.length > 0 ? (
          <List
            dataSource={notificationState.recentNotifications}
            renderItem={(notification) => (
            <List.Item
              style={{
                padding: '12px 16px',
                borderBottom: '1px solid #f0f0f0',
                backgroundColor: notification.read_at ? '#fafafa' : '#e6f7ff',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = notification.read_at ? '#f0f0f0' : '#d4edda';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = notification.read_at ? '#fafafa' : '#e6f7ff';
              }}
              onClick={() => {
                // 可以在这里添加点击通知的处理逻辑
                navigate('/notifications/logs');
                setNotificationDropdownVisible(false);
              }}
            >
              <List.Item.Meta
                avatar={getNotificationIcon(notification.trigger_type || 'test', notification.status || 'success')}
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text 
                      style={{ 
                        fontSize: 13,
                        fontWeight: notification.read_at ? 'normal' : 'bold',
                        maxWidth: '200px',
                      }}
                      ellipsis={{ tooltip: true }}
                    >
                      {notification.notification_config?.name || notification.message || '系统通知'}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11, flexShrink: 0, marginLeft: 8 }}>
                      {notification.sent_at ? formatNotificationTime(notification.sent_at) : '刚刚'}
                    </Text>
                  </div>
                }
                description={
                  <div>
                    <Text 
                      style={{ 
                        fontSize: 12, 
                        color: notification.read_at ? '#999' : '#666',
                        display: 'block',
                        marginBottom: 2,
                      }}
                      ellipsis={{ tooltip: notification.message || notification.url?.name }}
                    >
                      {notification.url?.name || notification.message || '无详细信息'}
                    </Text>
                    <Text 
                      type={notification.status === 'failed' ? 'danger' : 'secondary'}
                      style={{ fontSize: 11 }}
                    >
                      {notification.trigger_type === 'failure' ? '监控失败' : 
                       notification.trigger_type === 'recovery' ? '服务恢复' : 
                       notification.trigger_type === 'test' ? '测试通知' : '系统通知'}
                      {notification.status === 'failed' && ' (发送失败)'}
                    </Text>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="暂无通知"
          style={{ padding: '20px' }}
        />
      )}
      </div>
      
      {notificationState.recentNotifications.length > 0 && (
        <div 
          className="notification-dropdown-footer"
          style={{ 
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Button 
            type="link" 
            size="small"
            style={{ 
              fontSize: '13px',
              fontWeight: 500,
              color: '#1890ff',
              padding: '4px 8px',
              height: 'auto'
            }}
            onClick={() => {
              navigate('/notifications/logs');
              setNotificationDropdownVisible(false);
            }}
          >
            查看所有通知
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <AntHeader
      style={{
        background: '#fff',
        padding: '0 24px',
        borderBottom: '1px solid #f0f0f0',
        position: 'fixed',
        top: 0,
        right: 0,
        left: state.sidebarCollapsed ? 80 : 240,
        zIndex: 999,
        height: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        transition: 'all 0.2s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Button
          type="text"
          icon={state.sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={toggleSidebar}
          style={{
            fontSize: '16px',
            width: 32,
            height: 32,
          }}
        />
      </div>

      <Space size="middle">
        {/* 通知 */}
        <Dropdown
          dropdownRender={() => notificationDropdown}
          placement="bottomRight"
          trigger={['click']}
          open={notificationDropdownVisible}
          onOpenChange={setNotificationDropdownVisible}
        >
          <Tooltip title="通知">
            <Button
              type="text"
              onClick={handleNotificationClick}
              style={{
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
              className={notificationState.unreadCount > 0 ? 'notification-pulse' : ''}
            >
              <Badge 
                count={notificationState.unreadCount} 
                size="small"
                offset={[2, -2]}
              >
                <BellOutlined 
                  style={{ 
                    fontSize: '16px',
                    color: notificationState.unreadCount > 0 ? '#1890ff' : undefined,
                  }} 
                />
              </Badge>
            </Button>
          </Tooltip>
        </Dropdown>

        {/* 用户菜单 */}
        <Dropdown
          menu={{ items: userMenuItems }}
          placement="bottomRight"
          arrow
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: 6,
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Avatar
              size="small"
              icon={<UserOutlined />}
              style={{ marginRight: 8 }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 32, justifyContent: 'center', overflow: 'hidden' }}>
              <Text style={{ fontSize: 13, lineHeight: '16px', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>
                {state.user?.username || '用户'}
              </Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 0 }}>
                <Text type="secondary" style={{ fontSize: 11, lineHeight: '14px', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 100 }}>
                  {state.user?.email || ''}
                </Text>
                {state.user?.otp_enabled && (
                  <SafetyOutlined
                    style={{
                      color: '#52c41a',
                      fontSize: 11,
                    }}
                    title="已认证"
                  />
                )}
              </div>
            </div>
          </div>
        </Dropdown>
      </Space>
    </AntHeader>
  );
};

export default Header;